import React, { useEffect, useState } from 'react';
import { getProjectActivity, ActivityEntry } from '../lib/api';
import { I } from './Shared';

/**
 * "Who did what, when" feed for a project — the attribution trail for shared
 * apps. Renders each persisted event with the actor's name and a humanised
 * description derived from the cause.
 */
export function ActivityModal({
  projectId,
  appName,
  onClose,
}: {
  projectId: string;
  appName: string;
  onClose: () => void;
}) {
  const [items, setItems] = useState<ActivityEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const a = await getProjectActivity(projectId);
        if (!cancelled) setItems(a);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'failed to load activity');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <div className="hm-backdrop" onClick={onClose}>
      <div
        className="af card"
        style={{
          width: 600,
          maxHeight: '86%',
          padding: 0,
          background: 'var(--surface)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px var(--line)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="loz">ACTIVITY</span>
          <span className="t-h2" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{appName}</span>
          <span style={{ flex: 1 }} />
          {items && <span className="t-micro muted">{items.length} event{items.length === 1 ? '' : 's'}</span>}
          <button className="btn btn--ghost btn--sm btn--icon" onClick={onClose}>{I.x}</button>
        </div>

        {error && <div style={{ padding: 16, color: 'var(--err)' }}>{error}</div>}
        {!error && !items && <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-muted)' }}>Loading…</div>}
        {items && items.length === 0 && (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-muted)' }}>No activity recorded yet.</div>
        )}

        {items && items.length > 0 && (
          <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '8px 20px 16px' }}>
            {items.map((e) => (
              <div
                key={e.id}
                style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--line)' }}
              >
                <span style={{ fontWeight: 600, minWidth: 120, flexShrink: 0 }}>{e.actorName ?? 'System'}</span>
                <span style={{ flex: 1 }}>{describe(e)}</span>
                <span className="t-small muted t-num" style={{ flexShrink: 0 }}>{when(e.ts)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Turn a cause string ('edit:mkt-research', 'step.completed', …) into prose. */
function describe(e: ActivityEntry): string {
  const cause = e.cause ?? e.kind;
  if (cause === 'bootstrap') return 'created the app';
  const [verb, target] = cause.includes(':') ? cause.split(':') : [cause, e.stepKey ?? ''];
  const step = target || e.stepKey || '';
  switch (verb) {
    case 'edit':
      return `improved “${step}”`;
    case 'answer':
      return `answered questions on “${step}”`;
    case 'pin':
      return `pinned “${step}”`;
    case 'unpin':
      return `unpinned “${step}”`;
    case 'outdated':
      return `marked steps outdated after “${step}”`;
    case 'step':
      return e.stepKey ? `ran “${e.stepKey}”` : 'ran a step';
    case 'step.completed':
      return e.stepKey ? `completed “${e.stepKey}”` : 'completed a step';
    default:
      return e.stepKey ? `${verb.replace(/[._]/g, ' ')} “${e.stepKey}”` : verb.replace(/[._]/g, ' ');
  }
}

function when(ts: string): string {
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return d.toLocaleDateString();
}
